/*
  The handlers' key vocabulary, and the two questions asked of it.

  Every command records what it wrote as a list of keys — `bucket:E4`, `head:as-is:G:lvl1=Ops`,
  `target:unit:["Commercial","Sales"]`. Two very different callers read them:

    · the SERVER, refusing to revert a change somebody has built on top of, by comparing one event's keys
      against a later event's;
    · the CLIENT, asking whether an event that just arrived changed the thing the reader has open.

  Both live here, together, because the alternative is what this codebase has already been bitten by once:
  `DESTRUCTIVE_INVERSES` was a hand-written list in the server of a property the handlers own, and it was
  missing two entries. A key vocabulary re-implemented on the client would be the same mistake with more
  surface. `keysOverlap` moved here from `apps/server/src/revert.ts` unchanged so there is exactly one.
*/
/** A target unit key names a BRANCH, so a prefix in either direction is the same tree. */
const UNIT_PREFIX = 'target:unit:';
/** The path inside a `target:unit:` key, which the handlers encode as a JSON array. */
function unitPath(key) {
    if (!key.startsWith(UNIT_PREFIX))
        return null;
    try {
        const parsed = JSON.parse(key.slice(UNIT_PREFIX.length));
        return Array.isArray(parsed) && parsed.every((s) => typeof s === 'string') ? parsed : null;
    }
    catch {
        return null;
    }
}
/**
 * Two keys name the same thing, or one contains the other.
 *
 * Everything is exact except a unit path, which is compared segment by segment rather than as text —
 * the keys are JSON, so `["A","B"]` is not a string prefix of `["A","B","C"]`, and a naive
 * `startsWith` would also make `["Ops"]` look like a parent of `["Opsy"]`.
 */
export function keysOverlap(a, b) {
    if (a === b)
        return true;
    const pa = unitPath(a);
    const pb = unitPath(b);
    if (!pa || !pb)
        return false;
    const [shorter, longer] = pa.length <= pb.length ? [pa, pb] : [pb, pa];
    return shorter.every((segment, i) => longer[i] === segment);
}
/**
 * The target-unit path a chart node id names, or null when it is not one.
 *
 * A group node id is content-addressed on its resolved level path (`groupNodeId`, `hierarchy.ts`), and a
 * TARGET unit's levels are the `tlvlN` ladder — so a node whose every segment is a target level is a
 * declared unit, and its values in order are the path the `target:unit:` keys carry.
 *
 * Every segment must be a target level. A node mixing them with as-is levels is not a unit, and answering
 * as though it were would make an ordinary group match target keys.
 */
function targetUnitPath(nodeId) {
    const segments = nodeId.slice('G:'.length).split('||');
    const path = [];
    for (const segment of segments) {
        const eq = segment.indexOf('=');
        if (eq === -1 || !/^tlvl[0-9]+$/.test(segment.slice(0, eq)))
            return null;
        path.push(segment.slice(eq + 1));
    }
    return path.length ? path : null;
}
/**
 * Does this change touch the node a reader has open?
 *
 * The client's question, and deliberately a NARROWER one than the server's. The revert guard asks "could
 * this later change have been built on that earlier one", and errs toward refusing. This asks "is the
 * thing on screen now different because of what just arrived", and errs the other way: a notice nobody
 * needed is noise on every keystroke somebody else makes, and noise is how a warning stops being read.
 *
 * So only the keys that name THIS node count. A colleague renaming a level, editing an assumption or
 * moving somebody else does change your screen — the chart redraws — and does not get a notice, because
 * the notice exists for the specific surprise of the panel in front of you describing something that has
 * moved underneath it.
 *
 * ON IDS THAT CONTAIN A COLON. Employee ids come from client spreadsheets, so `employee:A:B:x` is
 * genuinely ambiguous between id `A` field `B:x` and id `A:B` field `x`. Prefix matching resolves it
 * toward MORE notices rather than fewer, which is the safe direction for a message that says "look at
 * this again" and does nothing else.
 */
export function touchesNode(touched, nodeId) {
    if (!nodeId)
        return false;
    /*
      A group id is a full path from the first level, generated content-addressed. Heads are the only
      per-group state a command records against one — a group rename is keyed by the LEVEL VALUE
      (`groupRename:as-is:lvl1:Ops`), which is a different thing and deliberately not matched here: it
      renames every group with that value across the project, and one of them being on your screen is not
      a change to the group you have open.
    */
    if (nodeId.startsWith('G:')) {
        /*
          A TARGET UNIT IS A GROUP NODE THAT NO HEAD KEY CAN EVER NAME, so before this the branch above was
          not merely narrow for one — it was empty. Head designation is deliberately removed under the target
          view, and every target-unit command records `target:unit:` / `target:value:` instead, so a declared
          unit was a selectable node with its own editing panel that no notice could fire on. A colleague
          adding a child under the unit you have open, removing it, or re-parenting it raised nothing; on a
          remove or a repath the panel silently swapped to "The whole organisation…" with no attribution.
    
          Matched by rebuilding this node's path into the key the handlers write and handing BOTH to
          `keysOverlap`, rather than by a second prefix rule written out here. That is the same comparison the
          revert guard makes, so the two cannot drift — and it gives the right answer in both directions for
          free: a change to an ancestor takes this node with it, and a change to a descendant changes what
          this node's panel lists.
    
          The RENAME case stays out, and that is deliberate rather than an omission: `renameTargetValue` is
          keyed by VALUE, like a group rename, and renames it wherever it appears — so one of them being on
          your screen is not a change to the unit you have open. Same rule, same reason, as the group-rename
          exclusion this branch already documents.
        */
        const unit = targetUnitPath(nodeId);
        if (unit !== null) {
            const mine = `${UNIT_PREFIX}${JSON.stringify(unit)}`;
            if (touched.some((key) => keysOverlap(mine, key)))
                return true;
        }
        return touched.some((key) => {
            if (!key.startsWith('head:'))
                return false;
            const afterPerspective = key.indexOf(':', 'head:'.length);
            return afterPerspective !== -1 && key.slice(afterPerspective + 1) === nodeId;
        });
    }
    /*
      A person, or a planned hire — the build-up shares the id space and the panels treat both as a node.
  
      THE `E:` PREFIX COMES OFF FIRST, and its absence here was a dead promise rather than a rough edge.
      This function takes a CHART NODE ID — that is what `announcedSelection` holds, and the `G:` branch
      above already assumes it — but the person branch compared it against the raw employee ids the
      handlers write. `personNodeId('E4')` is `E:E4`; every decision handler writes `bucket:E4`. So
      `touchesNode(['bucket:E4'], 'E:E4')` was false, and "changed by X while you were editing" — the
      single most-quoted sentence in P5 — could only ever fire for a group, because a head key embeds the
      `G:` id verbatim and happens to match.
  
      A BARE ID DELIBERATELY NO LONGER MATCHES. Accepting both would be the forgiving choice and is how
      this hides again: the unit tests passed precisely because they called `select('E1')`, a value the UI
      cannot produce, so the mutation testing scored inside the fiction. `touched.test.ts` now derives the
      id it passes from `personNodeId` itself, so the two encodings cannot drift apart again without a
      test failing.
    */
    const employeeId = nodeId.startsWith('E:') ? nodeId.slice(2) : null;
    if (employeeId === null)
        return false;
    return touched.some((key) => key === `bucket:${employeeId}`
        || key === `buildUp:${employeeId}`
        /*
          THE FOUR THE PERSON PANEL EDITS AND THIS DID NOT KNOW. Each of these handlers records exactly one
          key and none of them also writes a `bucket:` key — not even `setJoinStage`, which additionally
          flips the decision's source to manual. So a colleague setting the join stage, the Day 2 exit, the
          stranded plan or the target placement of the very person you have open changed the panel in front
          of you and said nothing, while a bucket move on the same person notified. All four name YOUR node,
          which is the rule this function states for itself.
        */
        || key === `joinStage:${employeeId}`
        || key === `day2Exit:${employeeId}`
        || key === `stranded:${employeeId}`
        || key === `target:placement:${employeeId}`
        /*
          A BARE `employee:<id>`, which the intake handlers write to mean "this whole person changed".
  
          `applyUpdateRun` records it for every mover and `rebuildRosterFromSource` for every re-derived
          row — and `rebuildRosterFromSource`'s key list contains nothing else, so a re-map raised nothing
          at all. Matched exactly rather than by prefix, which keeps it unambiguous against ids that
          themselves contain a colon; the field-qualified form below is the one that has to be a prefix.
        */
        || key === `employee:${employeeId}`
        || key.startsWith(`employee:${employeeId}:`)
        // `override:<perspective>:<employeeId>:<fieldKey>` — the perspective sits in between, so this is
        // the one that cannot be a plain prefix test.
        || /^override:[^:]*:/.test(key) && key.slice(key.indexOf(':', 'override:'.length) + 1)
            .startsWith(`${employeeId}:`));
}
//# sourceMappingURL=touched.js.map